class FfPredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector("#ff-header-search-input");
    this.ffSuggestionsEl = this.querySelector(
      "#ff-predictive-search-suggestions"
    );
    this.predictiveSearchResults = this.querySelector(
      "#ff-predictive-search-results"
    );
    this.allPredictiveSearchInstances = document.querySelectorAll(
      "ff-predictive-search"
    );

    this.ffnoResultsNotifierEl = this.querySelector(
      "[data-ff-predictive-search-empty]"
    );
    this.abortController = new AbortController();
    this.searchTerm = "";

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );

    this.querySelectorAll("[data-ff-header-search-suggested-query]").forEach(
      (btn) => {
        btn.addEventListener("click", () => {
          const eventObj = new InputEvent("input");
          this.input.value = btn.textContent.trim();
          this.input.dispatchEvent(eventObj);
        });
      }
    );

    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const newSearchTerm = this.getQuery();
    this.searchTerm = newSearchTerm;

    if (this.searchTerm.length > 2) {
      this.getSearchResults(this.searchTerm);
    } else {
      this.closeResults();
      return;
    }
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&section_id=ff-predictive-search`,
      {
        signal: this.abortController.signal,
      }
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.closeResults(true);
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#ff-predictive-search-results").innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          }
        );
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.closeResults(true);
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    if (this.querySelector("#predictive-search-results-products-list")) {
      this.ffSuggestionsEl.classList.add("ff-d-none");
      this.predictiveSearchResults.classList.remove("ff-d-none");
      this.ffnoResultsNotifierEl.classList.add("ff-d-none");
    } else {
      this.ffSuggestionsEl.classList.remove("ff-d-none");
      this.ffnoResultsNotifierEl.textContent = this.querySelector(
        "[data-predictive-search-live-region-count-value]"
      ).textContent;

      this.predictiveSearchResults.classList.add("ff-d-none");
      this.ffnoResultsNotifierEl.classList.remove("ff-d-none");
    }

    this.setLiveRegionResults(resultsMarkup);
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        .textContent
    );
  }

  open() {
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);

    this.ffUpdateHeight();
  }

  ffUpdateHeight() {
    const tabContentHeight = this.children[0].clientHeight;
    this.style.setProperty("--ff-expanded-height", tabContentHeight + "px");

    this.style.setProperty(
      "--ff-collapsible-tabs-transition-seconds",
      tabContentHeight * 0.0004 + 0.06 + "s"
    );
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }

    this.ffSuggestionsEl.classList.remove("ff-d-none");
    this.predictiveSearchResults.classList.add("ff-d-none");
    this.predictiveSearchResults.innerHTML = "";
    this.ffnoResultsNotifierEl.classList.add("ff-d-none");
    this.ffnoResultsNotifierEl.textContent = "";

    this.ffUpdateHeight();

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.removeAttribute("loading");
    this.removeAttribute("open");
  }
}

customElements.define("ff-predictive-search", FfPredictiveSearch);
